<template>
  <page layoutClass="manage-users">
    <b-row class="mb-3">
      <b-col cols="12" sm="7">
        <h2 class="m-0">Talentpool</h2>
      </b-col>
      <b-col cols="12" sm="5" class="d-flex flex-row justify-content-end align-items-center">

        <b-button class="mr-3" size="md" variant="outline-primary" @click.prevent="() => showFilters = !showFilters">
          <font-awesome-icon icon="filter"/>
          <span class="d-none d-sm-inline-block">Filter</span>
        </b-button>

        <b-card class="shadow" body-class="d-flex flex-row justify-content-between align-items-center p-1">
          <b-form-select :options="userOrderByEnums" :style="{ width: '200px' }" v-model="filterUsersOrder"/>
        </b-card>
      </b-col>
    </b-row>

    <error-block v-if="allUsersErr" class="mb-3" :err="allUsersErr"/>

    <b-card class="shadow mb-3" v-if="showFilters">
      <boolean-search :fields="searchFields" :value="filterUsersWhere" @input="filterUsersWhereUpdated"/>
    </b-card>

    <b-card class="shadow p-0" no-body>
      <!-- selectable select-mode="range" selectedVariant="success" @row-selected="onItemSelected" -->
      <b-table table-class="m-0" hover striped small responsive show-empty
        :busy="$apollo.queries.userConnections.loading" :fields="fields" :items="items">
          <template #cell(avatar)="{ item }">
            <avatar :user="item" :size="30"/>
          </template>
          <template #cell(fullName)="{ item }">
            <div style="cursor: pointer" @click="openProfile(item)">{{ item.fullName }}</div>
          </template>
          <template #cell(mentorStatus)="{ item }">
            <div>
              <div
                :style="{
                  cursor: 'pointer',
                  display: 'inline-block',
                  'background-color': item.mentorStatus ? statusColors(item.mentorStatus) : 'rgb(226 232 240)',
                  'border-radius': '3px'
                }"
                class="px-2"
                @click="$bvModal.show(`update-status-modal-${item.id}`)">
                {{ item.mentorStatus ? 'Mentor' : 'Employee' }}
              </div>

              <b-modal
                v-if="item.mentorStatus"
                :id="`update-status-modal-${item.id}`"
                size="sm"
                title="Update Mentor Status"
                hide-footer
                lazy
              >
                <update-mentor-status-form
                  :value="item.mentorStatus"
                  role="admin"
                  @input="updateMentorStatus({ mentorStatus: $event, userID: item.id })"
                />
              </b-modal>
            </div>
          </template>

          <template #cell(isTalent)="{ item }">
            <div>
              <div
                :style="{
                  cursor: 'pointer',
                  display: 'inline-block',
                  'background-color': item.isTalent ? 'rgb(253 230 138)' : 'rgb(226 232 240)',
                  'border-radius': '3px'
                }"
                class="px-2">
                {{ item.isTalent ? 'Yes' : 'No' }}
              </div>
            </div>
          </template>

          <template #cell(matchedJobConnections.total)="{ item }">
            <div>
              <div
                :style="{ cursor: 'pointer', display: 'inline-block', 'background-color': item.matchedJobConnections.total ? 'rgb(253 230 138)' : 'rgb(226 232 240)', 'border-radius': '3px' }"
                class="px-2"
                @click="$bvModal.show(`matched-jobs-modal-${item.id}`)">
                {{ item.matchedJobConnections ? item.matchedJobConnections.total : 0 }} job{{ item.matchedJobConnections.total > 1 ? 's' : ''}}/role{{ item.matchedJobConnections.total > 1 ? 's' : ''}}
              </div>

              <b-modal
                v-if="item.matchedJobConnections ? item.matchedJobConnections.total : false"
                :id="`matched-jobs-modal-${item.id}`"
                size="lg"
                :title="`Matched Jobs of ${item.fullName} (${item.emailAddress})`"
                hide-footer
              >
                <internal-job-pool-container
                  class="mt-4"
                  :talent="item"
                  :jobs="Array.isArray(item.matchedJobConnections.edges) ? item.matchedJobConnections.edges.map(({ entry }) => entry) : []"
                  @sendContactEmail="sendContactEmail($event)"
                />
              </b-modal>

            </div>
          </template>

          <template #cell(matchedMentorConnections.total)="{ item }">
            <div>
              <div
                :style="{ cursor: 'pointer', display: 'inline-block', 'background-color': item.matchedMentorConnections.total ? 'rgb(253 230 138)' : 'rgb(226 232 240)', 'border-radius': '3px' }"
                class="px-2"
                @click="$bvModal.show(`matched-mentors-modal-${item.id}`)">
                {{ item.matchedMentorConnections ? item.matchedMentorConnections.total : 0 }} mentor{{ item.matchedMentorConnections.total > 1 ? 's' : ''}}
              </div>

              <b-modal
                v-if="item.matchedMentorConnections ? item.matchedMentorConnections.total : false"
                :id="`matched-mentors-modal-${item.id}`"
                size="lg"
                :title="`Matched Mentors of ${item.fullName} (${item.emailAddress})`"
                hide-footer
              >
                <matched-mentors-container
                  class="mt-4"
                  :matchedMentors="Array.isArray(item.matchedMentorConnections.edges) ? item.matchedMentorConnections.edges.map(({ entry }) => entry) : []"
                />
              </b-modal>

            </div>
          </template>

          <template #cell(mentorRequests)="{ item }">
            <div>
              <div
                :style="{ cursor: 'pointer', display: 'inline-block', 'background-color': item.mentorRequests && item.mentorRequests.length ? 'rgb(165 243 252)' : 'rgb(226 232 240)', 'border-radius': '3px' }"
                class="px-2"
                @click="$bvModal.show(`mentor-requests-modal-${item.id}`)">
                {{ item.mentorRequests ? item.mentorRequests.length : 0 }} mentor{{ item.mentorRequests.length > 1 ? 's' : ''}}
              </div>

              <b-modal
                v-if="item.mentorRequests ? item.mentorRequests.length : false"
                :id="`mentor-requests-modal-${item.id}`"
                size="lg"
                :title="`Mentors of ${item.fullName} (${item.emailAddress})`"
                hide-footer
              >
                <mentor-requests-container
                  class="mt-4"
                  :mentorRequests="item.mentorRequests"
                />
              </b-modal>
            </div>
          </template>

          <template #cell(opts)="{ item }">
            <b-button
              v-if="isRecruiterAdmin"
              class="float-right"
              @click.prevent="openProfile(item)"
              size="sm"
              variant="outline-primary"
            >
              View Profile
            </b-button>
          </template>
      </b-table>

      <b-card class="mt-3 border-0" body-class="d-flex flex-row p-3 justify-content-center align-items-center" v-if="userConnections && userConnections.total">
        <b-pagination class="m-0" v-model="filterUsersPage" :per-page="userConnections.limit" :total-rows="userConnections.total"/>
      </b-card>

    </b-card>

    <!-- Show Profile Modal -->
    <b-modal
      id="profile-show-modal"
      title="Profile"
      body-class="gray-bg"
      size="lg"
      hide-footer
      lazy
    >
      <profile-show
        :company="selectedUser.company"
        :user="selectedUser.value"
        layout="modal"
      />
    </b-modal>

  </page>
</template>

<script>
import Page from '@/components/Page';
import enumsMixin from '@/mixins/enums';
import formatDate from 'date-fns/format';
import ErrorBlock from '@/components/misc/ErrorBlock';
import UsersListQuery from '@/graphql/queries/UserListInternalMobility.gql';
import UserEditQuery from '@/graphql/queries/UserEdit.gql';
import BooleanSearch from '@/components/misc/BooleanSearch';
import EditProfileForm from '@/components/users/EditProfileForm';
import ProfileShow from '@/components/profile/ProfileShow';
import UpdateMentorStatusForm from '@/components/mentors/UpdateMentorStatusForm';
import ProfileEditor from '@/components/profile/ProfileEditor';
import { mapRoot } from '@src/mixins/root';
import Avatar from '@/components/users/Avatar';
import recommendJobEmailTemplate from '@/emails/recommendJob';
import genericContactEmailTemplate from '@/emails/genericContact';
import SendEmailMutation from '@/graphql/mutations/SendEmail.gql';
import InternalJobPoolContainer from '@/components/projects/InternalJobPoolContainer.vue';
import MentorRequestsContainer from '@/components/mentors/MentorRequestsContainer.vue';
import MatchedMentorsContainer from '@/components/mentors/MatchedMentorsContainer.vue';

const emailQuery = /* GraphQL */`
  query CustomTemplateQuery {
    company {
      id
      name
      domain(where: { primary: true }) {
        domain(prefix:"https://")
        emailFrom
      }
      branding {
        name
        bigLogo {
          id
          fileLocation
        }
        smallLogo {
          id
          fileLocation
        }
        emailHeaderImage {
          id
          fileLocation
        }
        headerFont
        bodyFont
        primaryColour
        secondaryColour
        backgroundColour
      }
    }
    user {
      id
      firstName
      fullName
      emailAddress
      title
    }
  }
`;

export default {
  components: {
    Page,
    ErrorBlock,
    BooleanSearch,
    EditProfileForm,
    ProfileShow,
    UpdateMentorStatusForm,
    InternalJobPoolContainer,
    MentorRequestsContainer,
    MatchedMentorsContainer,
    ProfileEditor,
    Avatar,
  },
  mixins: [
    enumsMixin,
  ],
  data: () => ({
    allUsersErr: null,
    selectedUserID: null,
    selectedUserErr: null,
    filterUsersPage: 1,
    filterUsersLimit: 100,
    filterUsersWhere: {
      status: 'ACTIVE',
    },
    filterUsersOrder: 'CREATED_ASC',
    showFilters: false,
    selectedUser: {
      company: null,
      value: null,
    },
  }),
  apollo: {
    userConnections: {
      query: UsersListQuery,
      variables() {
        const { filterUsersPage, filterUsersLimit, filterUsersWhere, filterUsersOrder } = this;
        const filter = JSON.parse(JSON.stringify(filterUsersWhere));
        if (filter.hasMatchedJobEnum === 'YES') {
          filter.hasMatchedJob = true;
          delete filter.hasMatchedJobEnum;
        } else {
          delete filter.hasMatchedJobEnum;
        }
        if (filter.hasMatchedMentorEnum === 'YES') {
          filter.hasMatchedMentor = true;
          delete filter.hasMatchedMentorEnum;
        } else {
          delete filter.hasMatchedMentorEnum;
        }
        if (filter.hasMentorRequestEnum === 'YES') {
          filter.hasMentorRequest = true;
          delete filter.hasMentorRequestEnum;
        } else {
          delete filter.hasMentorRequestEnum;
        }
        if (filter.isTalentEnum === 'YES') {
          filter.isTalent = true;
          delete filter.isTalentEnum;
        } else {
          delete filter.isTalentEnum;
        }
        console.log('filterUsersWhere', filter);
        return { filterUsersPage, filterUsersLimit, filterUsersWhere: filter, filterUsersOrder };
      },
      update({ company: { userConnections } }) {
        return userConnections;
      },
      error(err) {
        this.allUsersErr = err;
      },
      fetchPolicy: 'network-only',
      deep: true,
    },
    selectedUser: {
      query: UserEditQuery,
      variables() {
        const { selectedUserID: userID } = this;
        return { userID };
      },
      update: ({ company: { value, ...company } }) => ({ company, value }),
      error(err) {
        this.selectedUserErr = err;
      },
      skip() {
        return !this.selectedUserID || [ 'ADD', 'IMPORT' ].includes(this.selectedUserID);
      },
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'isRecruiterAdmin', 'allowInvitations' ]),
    fields() {
      return [
        {
          key: 'avatar',
          label: '',
          headerTitle: 'Avatar',
          thStyle: { width: '50px' },
        },
        {
          key: 'fullName',
          label: 'Name',
          thStyle: { width: '150px' },
        },
        {
          key: 'mentorStatus',
          label: 'Mentor',
          formatter: value => value || 'N/A',
          thStyle: { width: '100px' },
        },
        {
          key: 'isTalent',
          label: 'Talent',
          formatter: value => value || 'N/A',
          thStyle: { width: '100px' },
        },
        // {
        //   key: 'customFields',
        //   label: 'Title',
        //   formatter(value) {
        //     if (Array.isArray(value)) {
        //       const title = value.find(({ key }) => key && key.includes('JOB-TITLE'));
        //       return title ? title.value : ' - ';
        //     }
        //     return ' - ';
        //   },
        //   thStyle: { width: '300px' },
        // },
        {
          key: 'matchedJobConnections.total',
          label: 'Job/Role Matches',
          thStyle: { width: '200px' },
        },
        {
          key: 'matchedMentorConnections.total',
          label: 'Mentor Matches',
          thStyle: { width: '160px' },
        },
        {
          key: 'mentorRequests',
          label: 'Mentor Relationships',
          thStyle: { width: '180px' },
        },
        {
          key: 'emailAddress',
          label: 'Email Address',
          thStyle: { width: '260px' },
        },
        {
          key: 'created',
          label: 'Created',
          formatter: value => formatDate(value, 'YYYY-MM-DD HH:mm'),
          thStyle: { width: '200px' },
        },
        {
          key: 'opts',
          label: '',
          headerTitle: 'Options',
          thStyle: { width: '120px' },
        },
      ];
    },
    items() {
      const { edges } = this.userConnections || {};
      if (Array.isArray(edges) && edges.length) {
        return edges.reduce((list, row) => {
          if (row && row.entry && row.entry.id && row.entry.role !== 'SUPER_ADMIN') {
            list.push(row.entry);
          }
          return list;
        }, []);
      } else {
        return [];
      }
    },
    searchFields() {
      return {
        name: {
          label: 'Name',
          placeholder: 'First or Last Name...',
          type: 'STRING',
        },
        emailAddress: {
          label: 'Email Address',
          placeholder: '...@example.com',
          type: 'STRING',
        },
        mentorStatus: {
          label: 'Mentor Status',
          type: 'ENUM',
          options: {
            REQUESTED: 'Requested',
            REJECTED: 'Rejected',
            ACTIVE: 'Active',
            HIDDEN: 'Hidden',
            INACTIVE: 'Inactive'
          },
          empty: 'All',
        },
        status: {
          label: 'Status',
          type: 'ENUM',
          options: this.userStatusEnums,
        },
        hasMatchedJobEnum: {
          label: 'Job Matches',
          type: 'ENUM',
          options: {
            'YES': 'Matched to job',
          },
          empty: 'All',
        },
        hasMatchedMentorEnum: {
          label: 'Mentor Matches',
          type: 'ENUM',
          options: {
            'YES': 'Matched to mentor',
          },
          empty: 'All',
        },
        hasMentorRequestEnum: {
          label: 'Mentor Requests',
          type: 'ENUM',
          options: {
            'YES': 'Has requests',
          },
          empty: 'All',
        },
        isTalentEnum: {
          label: 'Talents',
          type: 'ENUM',
          options: {
            'YES': 'Talents',
          },
          empty: 'All',
        },
      };
    },
    page() {
      const { page } = this.userConnections;
      return page || 1;
    },
    limit() {
      const { limit } = this.userConnections;
      return limit || 10;
    },
    customFields() {
      const { selectedUser: { value: user, company: { customFields } = {} } = {} } = this;
      const userDepartments = Array.isArray(user.departments) ? user.departments.map(({ id }) => id) : [];
      const fields = (Array.isArray(customFields) ? customFields : []).map(({ options, departments, ...field }) => ({
        ...field,
        departments: departments ? departments.map(({ id }) => id) : null,
      })).filter(({ departments }) => departments ? departments.filter(el => userDepartments.includes(el)).length : true);
      return fields;
    },
    selectedUserCustomFieldValues() {
      const { selectedUser: { value: user } = {}, parseIfPossible } = this;
      if (user && Array.isArray(user.customFields) && user.customFields.length) {
        const values = user.customFields.reduce((values, { key, value }) => Object.assign(values, { [key]: parseIfPossible(value) }), {});
        return values;
      }
      return null;
    },
    mentorshipFormContent() {
      const { selectedUserCustomFieldValues: values, customFields } = this;
      const fields = customFields.filter(el => el.group === 'mentorship');
      return {
        name: 'mentorship',
        fields,
        values,
      };
    },
  },
  methods: {
    statusColors(key) {
      const colors = {
        REQUESTED: 'rgb(254 215 170)',
        REJECTED: 'rgb(254 205 211)',
        ACTIVE: 'rgb(187 247 208)',
        HIDDEN: 'rgb(226 232 240)',
        INACTIVE: 'rgb(226 232 240)'
      };

      return colors[key] || 'rgb(226 232 240)';
    },
    openEditProfile(item) {
      this.selectedUserID = item.id;
      this.$bvModal.show('profile-edit-modal');
    },
    openLineManagerEditProfile(item) {
      this.selectedUserID = item.id;
      this.$bvModal.show('line-manager-profile-edit-modal');
    },
    openProfile(item) {
      this.selectedUserID = item.id;
      this.$bvModal.show('profile-show-modal');
    },
    openEditMentorship(item) {
      this.selectedUserID = item.id;
      this.$bvModal.show('mentorship-edit-modal');
    },
    filterUsersWhereUpdated(where) {
      console.log('filterUsersWhereUpdated', where);
      this.filterUsersWhere = where;
      this.filterUsersPage = 1;
    },
    async closeSelectedUserModal() {
      try {
        await this.$apollo.queries.userConnections.refetch();
      } catch (err) {
        console.error(err);
      } finally {
        this.selectedUserErr = null;
        this.selectedUserID = null;
      }
    },
    parseIfPossible(value) {
      try {
        value = JSON.parse(value);
      } catch (e) {
        // not a json
      }
      return value;
    },
    getEmailBody({ message, fromName, subject: subjectEnum }, job) {
      const { siteBranding, sitePreferences } = this.company || {};
      const { emailHeaderImage, primaryColour, secondaryColour, backgroundColour, headerFont, bodyFont } = siteBranding || {};
      const { emailHideBrandLogo } = sitePreferences || {};

      const subject = subjectEnum === 'RECOMMEND_JOB' ? 'Recommended opportunity' : `${fromName} wants to contact you`;

      const brandingSettings = {
        headerImage: emailHeaderImage ? emailHeaderImage.fileLocation : '',
        hideBrandLogo: emailHideBrandLogo === true,
        primaryColour,
        secondaryColour,
        backgroundColour,
        bodyFont,
        headerFont,
      };

      const slots = [
        {
          content: message,
        }
      ];

      const params = {
        subject,
        slots,
        brandingSettings,
        jobID: job.id,
        jobTitle: job.title,
      };

      this.$assert(recommendJobEmailTemplate && typeof recommendJobEmailTemplate.mjml === 'function', 'Template not found');
      this.$assert(genericContactEmailTemplate && typeof genericContactEmailTemplate.mjml === 'function', 'Template not found');

      return subjectEnum === 'RECOMMEND_JOB' ? recommendJobEmailTemplate.mjml.call(this, params) : genericContactEmailTemplate.mjml.call(this, params);
    },
    async sendContactEmail({ contactForm, talent, job }) {
      try {
        this.isLoading = true;
        const { id: userID } = talent;

        // Manually check we have a userID to send to, otherwise we risk sending to everyone
        this.$assert(userID, new Error('Missing userID'));

        const email = {
          template: this.getEmailBody(contactForm, job),
          query: emailQuery,
          where: { users: { id: userID } },
        };

        if (contactForm.fromName && contactForm.fromName.length) {
          email.fromName = contactForm.fromName;
        }

        await this.$apollo.mutate({
          mutation: SendEmailMutation,
          variables: { email },
        });
        this.isLoading = false;
        setTimeout(() => this.$refs.testEmailSentSucessfullyModal.show(), 300);
      } catch (err) {
        this.$log.error(err);
        this.isLoading = false;
        setTimeout(() => this.$refs.emailSendErrorModal.show(), 300);
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/Recruiter/TalentPool.vue